<template>
  <div>
    <div class="mt-3 flex space-x-3 py-2">
      <UISwitch
        id="email-notification-switch"
        v-model:value="isNotificationsEnabled"
        :aria-label="t('alt_text.email_notification_switch')"
        :title="t('alt_text.email_notification_switch')"
        class="w-1/3"
        size="small"
        @update:value="
          value => {
            toggleNotification(value)
          }
        "
      />
      <div class="items-center text-sm font-medium text-gray-700">
        {{
          isNotificationsEnabled
            ? t('eventNotification.enabled')
            : t('eventNotification.disabled')
        }}
      </div>
    </div>
    <div class="my-2 text-sm font-medium text-gray-500">
      {{ t('eventNotification.email.notificationLabel') }}
    </div>

    <!-- Render all emails template of NotificationType - confirmation,reminder and others -->
    <UITooltip :trigger="!isNotificationsEnabled ? 'hover' : 'manual'">
      <template #trigger>
        <div
          :class="{ 'disabled-div hover:bg-gray-300': !isNotificationsEnabled }"
        >
          <!-- Use template wrapper to allow v-if + v-for on separate elements (Vue 3 best practice).
               Guest notifications are kept in state.eventNotifications even when enableGuests is
               false so the toggle-all payload includes them; they are just hidden from the UI. -->
          <template
            v-for="(emailNotification, i) in state.eventNotifications"
            :key="'email-notification-' + i"
          >
            <div
              v-if="emailNotification.receiverType !== 'guest' || AppState.editCalendarValidationData.enableGuests"
            >
              <!-- Render a single email configurator window -->
              <EmailTemplateConfigurator
                ref="emailTemplateConfigurator"
                :index="i"
                :class="{
                  'rounded-t-lg border-t border-l border-r': i === 0,
                  'border-l border-r border-t':
                    i !== 0 && i !== state.eventNotifications.length - 1,
                  'rounded-b-lg border-t border-l border-r border-b':
                    i === state.eventNotifications.length - 1,
                }"
                :notification="emailNotification"
                :is-notifications-enabled="isNotificationsEnabled"
              />
            </div>
          </template>
        </div>
      </template>
      <div>{{ t('eventNotification.tooltip') }}</div>
    </UITooltip>
  </div>
</template>

<script setup lang="ts">
import { EventNotificationService } from '@/class/services/EventNotificationService'
import { useNotify } from '@/components/composition/notification'
import AppState from '@/states/app'
import {
  getEventNotificationState,
  eventNotificationState as state,
} from '@/states/EventNotificationState'
import { cleanHTMLForEmail } from '@gohighlevel/ghl-text-editor'
import { UISwitch, UITooltip } from '@gohighlevel/ghl-ui'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import EmailTemplateConfigurator from './EmailTemplateConfigurator.vue'
const { t } = useI18n()
const { sendNotification } = useNotify()

const isNotificationsEnabled = ref(true)
const emailTemplateConfigurator = ref(null) as any
defineExpose({ validateAll })
const emit = defineEmits(['update-notification'])

isNotificationsEnabled.value = getEventNotificationState('email')
/**
 *
 * @param value boolean
 * enable/disable all the email notification under this
 */
async function toggleNotification(value) {
  const payload = state.eventNotifications.map(notification => {
    notification.body &&
      (notification.body = cleanHTMLForEmail(notification.body))
    return { ...notification, ...{ deleted: !value } }
  })
  state.disableSave = true
  const previousValue = isNotificationsEnabled.value
  try {
    isNotificationsEnabled.value = value

    const payloadToUpdate = payload.filter(notification => notification._id)
    const payloadToCreate = payload
      .filter(notification => !notification._id)
      .map((notification: any) => {
        delete notification._id
        return notification
      })
    if (payloadToUpdate.length) {
      const { data } = await EventNotificationService.updateEventNotifications(
        payloadToUpdate
      )
      // Update local state with payload values (not API response)
      payloadToUpdate.forEach((item, index) => {
        const stateIndex = state.eventNotifications.findIndex(
          n => n._id === item._id || n.receiverType === item.receiverType
        )
        if (stateIndex !== -1) {
          state.eventNotifications[stateIndex] = {
            ...state.eventNotifications[stateIndex],
            deleted: !value,
          }
          // Also update the EventNotification instance state (used by Save function)
          if (state[item.receiverType]?.state) {
            state[item.receiverType].state.deleted = !value
          }
        }
      })
    }
    if (payloadToCreate.length) {
      const { data } = await EventNotificationService.createEventNotifications(
        payloadToCreate
      )
      // Update local state with payload values + assign _id from response
      payloadToCreate.forEach((item, index) => {
        const stateIndex = state.eventNotifications.findIndex(
          n => n.receiverType === item.receiverType
        )
        if (stateIndex !== -1) {
          const newId = data?.[index]?._id || state.eventNotifications[stateIndex]._id
          state.eventNotifications[stateIndex] = {
            ...state.eventNotifications[stateIndex],
            deleted: !value,
            _id: newId,
          }
          // Also update the EventNotification instance state (used by Save function)
          if (state[item.receiverType]?.state) {
            state[item.receiverType].state.deleted = !value
            state[item.receiverType].state._id = newId
          }
        }
      })
    }
  } catch (error) {
    // Revert local state on error
    isNotificationsEnabled.value = previousValue
    sendNotification({
      type: 'error',
      title: 'failed to update email notification',
      icon: 'AlertCircleIcon',
    })
    return
  } finally {
    state.disableSave = false
  }
  //update calendar notification if enabled
  handleOuterNotification(value, payload[0].notificationType)
  sendNotification({
    type: 'success',
    title: value
      ? t('eventNotification.email.enableSuccess')
      : t('eventNotification.email.disableSuccess'),
    icon: 'CheckCircleIcon',
  })
}

function handleOuterNotification(value, type) {
  emit('update-notification', {
    channel: 'email',
    type: type,
    status: value,
  })
}

function validateAll() {
  if (emailTemplateConfigurator.value) {
    return Promise.all(
      emailTemplateConfigurator.value.map(formComponent => {
        if (formComponent && formComponent.validateForm) {
          return formComponent.validateForm()
        }
      })
    )
  }
  return Promise.resolve()
}
</script>

<style scoped>
.disabled-div {
  position: relative;
  user-select: none;
  opacity: 0.5;
}
</style>
